/**
 * @fileoverview Date preset selector for the desktop FlyingMenu filter bar.
 * Delegates all date-range state to FilterContext.
 * @module shared/components/filters/DatePresetSelector
 */
import { ReactElement, useCallback, useEffect } from 'react'
import { Calendar, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui'
import { cn } from '@/shared/utils/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Kbd } from '@/components/ui/kbd'
import { useFilter } from '@/shared/hooks/useFilter'
import { classifyDateRange, getCurrentMonthRange, monthRange, isMonthInRange } from '@/shared/utils/dateUtils'
import { useMonthRangeDrag } from '@/shared/hooks/useMonthRangeDrag'

/**
 * Date preset selector backed by FilterContext.
 */
export function DatePresetSelector(): ReactElement {
  const { t } = useTranslation('common')
  const { startDate, endDate, setDateRange } = useFilter()
  const monthNamesShort = [
    t('monthsShort.jan'),
    t('monthsShort.feb'),
    t('monthsShort.mar'),
    t('monthsShort.apr'),
    t('monthsShort.may'),
    t('monthsShort.jun'),
    t('monthsShort.jul'),
    t('monthsShort.aug'),
    t('monthsShort.sep'),
    t('monthsShort.oct'),
    t('monthsShort.nov'),
    t('monthsShort.dec'),
  ] as const
  const now = new Date()
  const currentMonthIndex = now.getMonth()
  const formatMonthLabel = (idx: number): string => monthNamesShort[idx] ?? ''

  const currentTimeRange = classifyDateRange(startDate, endDate)

  const handleTimeRangeChange = useCallback((value: string): void => {
    const now = new Date()
    let s: Date
    let e: Date = now

    switch (value) {
      case 'today':
        s = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        e = s
        break
      case 'yesterday': {
        const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
        s = y
        e = y
        break
      }
      case 'this-month':
        s = new Date(now.getFullYear(), now.getMonth(), 1)
        e = now
        break
      case 'previous-month':
        s = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        e = new Date(now.getFullYear(), now.getMonth(), 0)
        break
      case 'this-year':
        s = new Date(now.getFullYear(), 0, 1)
        e = now
        break
      default:
        ;({ startDate: s, endDate: e } = getCurrentMonthRange())
    }

    setDateRange(s, e)
  }, [setDateRange])

  const commitMonthSpan = useCallback(
    (loIdx: number, hiIdx: number): void => {
      const now = new Date()
      const year = now.getFullYear()
      const start = monthRange(year, loIdx, now).start
      const end = monthRange(year, hiIdx, now).end
      setDateRange(start, end)
    },
    [setDateRange]
  )

  const { dragRange, getMonthProps } = useMonthRangeDrag({ onCommit: commitMonthSpan })

  // Keyboard shortcuts for quick preset selection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      // Only trigger if no input/textarea is focused
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return
      }

      if (event.key === '1') {
        event.preventDefault()
        handleTimeRangeChange('this-month')
      } else if (event.key === '2') {
        event.preventDefault()
        handleTimeRangeChange('previous-month')
      } else if (event.key === '3') {
        event.preventDefault()
        handleTimeRangeChange('this-year')
      } else if (event.key.toLowerCase() === 't') {
        event.preventDefault()
        handleTimeRangeChange('today')
      } else if (event.key.toLowerCase() === 'y') {
        event.preventDefault()
        handleTimeRangeChange('yesterday')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleTimeRangeChange])

  const label = (() => {
    switch (currentTimeRange) {
      case 'today':
        return t('timeRanges.today')
      case 'yesterday':
        return t('timeRanges.yesterday')
      case 'this-month':
        return t('timeRanges.thisMonth')
      case 'previous-month':
        return t('timeRanges.previousMonth')
      case 'this-year':
        return t('timeRanges.thisYear')
      default: {
        if (currentTimeRange.startsWith('month-')) {
          const idx = Number.parseInt(currentTimeRange.replace('month-', ''), 10)
          if (!Number.isNaN(idx) && idx >= 0 && idx <= 11) {
            return formatMonthLabel(idx)
          }
        }
        return t('timeRanges.customRange')
      }
    }
  })()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="glass-button !h-10 !px-2 md:!px-3 lg:!px-4 py-2 flex items-center md:space-x-1.5 lg:space-x-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-muted-foreground hidden md:inline text-xs lg:text-sm">{label}</span>
          <ChevronDown className="w-4 h-4 text-muted-foreground hidden md:inline" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="glass-card w-64">
        <DropdownMenuRadioGroup value={currentTimeRange} onValueChange={handleTimeRangeChange}>
          <DropdownMenuRadioItem value="today" className="flex items-center justify-between">
            <span>{t('timeRanges.today')}</span>
            <Kbd>t</Kbd>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="yesterday" className="flex items-center justify-between">
            <span>{t('timeRanges.yesterday')}</span>
            <Kbd>y</Kbd>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          <span>{t('timeRanges.months')}</span>
          <span className="text-xs font-normal text-muted-foreground/50">
            {t('timeRanges.dragHint')}
          </span>
        </DropdownMenuLabel>
        <div className="grid grid-cols-3 gap-2 p-1 [touch-action:none] select-none">
          {(() => {
            const visibleMonths = monthNamesShort.slice(0, currentMonthIndex + 1)
            const monthSelected = visibleMonths.map((_, idx) =>
              dragRange
                ? idx >= dragRange.lo && idx <= dragRange.hi
                : isMonthInRange(idx, startDate, endDate, now.getFullYear(), now)
            )
            return visibleMonths.map((name, idx) => {
              const isSelected = monthSelected[idx] ?? false
              // Fuse adjacent selected pills (within a grid row) into one continuous
              // band: extend each fused edge halfway across the 8px grid gap and keep
              // a single facing border as a subtle divider between the months.
              const col = idx % 3
              const fuseLeft = isSelected && col !== 0 && (monthSelected[idx - 1] ?? false)
              const fuseRight = isSelected && col !== 2 && (monthSelected[idx + 1] ?? false)
              return (
                <Button
                  key={name}
                  variant="outline"
                  size="sm"
                  className={cn(
                    'h-8 relative',
                    // Subtle selected tint: full-strength input glass (2x the hover tint).
                    // The dark:-scoped classes are required so twMerge drops the outline
                    // variant's dark:bg-input/* (which otherwise hides the highlight).
                    isSelected &&
                      'bg-input text-foreground hover:bg-input hover:text-foreground dark:bg-input dark:hover:bg-input',
                    fuseLeft &&
                      'rounded-l-none -ml-1 border-l-foreground/25 dark:border-l-foreground/25',
                    fuseRight && 'rounded-r-none -mr-1 border-r-0'
                  )}
                  {...getMonthProps(idx)}
                >
                  {formatMonthLabel(idx)}
                  {idx === currentMonthIndex && (
                    <span
                      aria-hidden
                      className="absolute top-1 right-1 size-1 rounded-full bg-foreground/50"
                    />
                  )}
                </Button>
              )
            })
          })()}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
